import last from 'lodash/last';



const DEFAULT_POSITION = { line: 1, column: 1 };
const HTML_TAG_REGEX = /<\/?([a-z0-9]+)(?:[^>]*?)(\/)?>/gi;
const HTML_STRUCTURE_REGEX = /<[^>]+>.*<\/[^>]+>/s;
const HTML_TAGS_PRESENCE_REGEX = /<[a-z][\s\S]*>/i;

const VALID_HTML_TAGS = [
  'a', 'abbr', 'acronym', 'address', 'applet', 'area', 'article',
  'aside', 'audio', 'b', 'base', 'basefont', 'bdi', 'bdo', 'bgsound',
  'big', 'blink', 'blockquote', 'body', 'br', 'button', 'canvas', 'caption',
  'center', 'cite', 'code', 'col', 'colgroup', 'content', 'data', 'datalist', 'dd',
  'decorator', 'del', 'details', 'dfn', 'dir', 'div', 'dl', 'dt', 'element', 'em', 'embed',
  'fieldset', 'figcaption', 'figure', 'font', 'footer', 'form', 'frame', 'frameset',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html', 'i', 'iframe', 'img',
  'input', 'ins', 'isindex', 'kbd', 'keygen', 'label', 'legend', 'li', 'link', 'listing', 'main', 'map',
  'mark', 'marquee', 'menu', 'menuitem', 'meta', 'meter', 'nav', 'nobr', 'noframes', 'noscript', 'object',
  'ol', 'optgroup', 'option', 'output', 'p', 'param', 'plaintext', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby',
  's', 'samp', 'script', 'section', 'select', 'shadow', 'small', 'source', 'spacer', 'span', 'strike', 'strong',
  'style', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'template', 'textarea', 'tfoot', 'th', 'thead',
  'time', 'title', 'tr', 'track', 'tt', 'u', 'ul', 'var', 'video', 'wbr', 'xmp',
];

const REQUIRED_ATTRIBUTES = {
  img: [ 'src', 'alt' ],
  audio: [ 'src' ],
  video: [ 'src' ],
  source: [ 'src' ],
  track: [ 'src', 'kind' ],
  a: [ 'href' ],
  link: [ 'href', 'rel' ],
  form: [ 'action' ],
  input: [ 'type' ],
  button: [ 'type' ],
  option: [ 'value' ],
  optgroup: [ 'label' ],
  iframe: [ 'src' ],
  td: [ 'colspan', 'rowspan' ],
  th: [ 'colspan', 'rowspan' ],
  q: [ 'cite' ],
  blockquote: [ 'cite' ],
  html: [ 'lang' ],
  time: [ 'datetime' ],
  progress: [ 'value', 'max' ],
  meter: [ 'value', 'min', 'max' ],
};

const INVALID_PARENT_CHILD = {
  'td': [ 'table' ],
  'tr': [ 'table', 'thead', 'tbody', 'tfoot' ],
  'li': [ 'ul', 'ol' ],
  'option': [ 'select', 'datalist' ],
};

const ATTRIBUTE_PATTERNS = {
  'href': /^.+$/,
  'src': /^.+$/,
  'id': /^[A-Za-z][\w:.-]*$/,
  'class': /^[\w\s-]+$/,
};


// Типы сообщений об ошибках
const ERROR_MESSAGES = {
  EMPTY_HTML: (intl) => intl.formatMessage({ id: 'templates-visualizations-form-empty-html-error' }),
  NOT_HTML_MARKUP: (intl) => intl.formatMessage({ id: 'templates-visualizations-form-no-html-error' }),
  INVALID_STRUCTURE: (intl) => intl.formatMessage({ id: 'templates-visualizations-form-html-invalid-structure-error' }),
  SYNTAX_ERROR: (intl) => intl.formatMessage({ id: 'templates-visualizations-form-html-syntax-error' }),
  UNKNOWN_TAG: (tag, intl) => `${intl.formatMessage({ id: 'templates-visualizations-form-html-unknown-tag-error' })}: <${tag}>`,
  UNCLOSED_TAG: (tag, intl) => `${intl.formatMessage({ id: 'templates-visualizations-form-html-unclosed-tag-error' })}: <${tag}>`,
  MISSING_OPENING_TAG: (tag, intl) => `${intl.formatMessage({ id: 'templates-visualizations-form-html-closing-tag-error-1' })} </${tag}> ${intl.formatMessage({ id: 'templates-visualizations-form-html-closing-tag-error-2' })}`,
  WRONG_CLOSING_ORDER: (expected, found, intl) => `${intl.formatMessage({ id: 'templates-visualizations-form-html-wrong-closing-order-error-1' })} </${expected}>${intl.formatMessage({ id: 'templates-visualizations-form-html-wrong-closing-order-error-2' })} </${found}>`,
  MISSING_ATTRIBUTE: (attr, tag, intl) => `${intl.formatMessage({ id: 'templates-visualizations-form-html-missing-attribute-error-1' })} "${attr}"${intl.formatMessage({ id: 'templates-visualizations-form-html-missing-attribute-error-2' })} <${tag}>`,
  EMPTY_ATTRIBUTE: (attr, tag, intl) => `${intl.formatMessage({ id: 'templates-visualizations-form-html-empty-attribute-error-1' })} "${attr}"${intl.formatMessage({ id: 'templates-visualizations-form-html-missing-attribute-error-2' })} <${tag}> ${intl.formatMessage({ id: 'templates-visualizations-form-html-empty-attribute-error-2' })}`,
  INVALID_PARENT: (child, validParents, intl) => `${intl.formatMessage({ id: 'templates-visualizations-form-html-invalid-parent-error-1' })} <${child}> ${intl.formatMessage({ id: 'templates-visualizations-form-html-invalid-parent-error-2' })} ${validParents.join(', ')}`,
  INVALID_ATTRIBUTE_VALUE: (attr, pattern, intl) => `${intl.formatMessage({ id: 'templates-visualizations-form-html-invalid-attribute-value-error-1' })} "${attr}"${intl.formatMessage({ id: 'templates-visualizations-form-html-invalid-attribute-value-error-2' })} ${pattern}`,
  DUPLICATE_ID: (id, intl) => `${intl.formatMessage({ id: 'templates-visualizations-form-html-duplicate-id-error-1' })} "${id}" ${intl.formatMessage({ id: 'templates-visualizations-form-html-duplicate-id-error-2' })}`,
  CRITICAL_ERROR: (intl) => intl.formatMessage({ id: 'templates-visualizations-form-html-critical-error' }),
};


/**
 * Вычисляет позицию ошибки в тексте
 * @param {string} text - Исходный HTML текст
 * @param {number} index - Индекс позиции ошибки
 * @returns {Object} Объект с номером строки и колонки
 */
const calculateErrPosition = ((text, index) => {
  const lines = text.slice(0, index).split('\n');

  return {
    line: lines.length,
    column: last(lines).length + 1,
  };
});


/**
 * Основная функция валидации HTML
 * Проверяет корректность HTML разметки, структуры и атрибутов
 * @param {string} html - HTML код для проверки
 * @param {string} intl - Перевод текста ошибки
 * @returns {Array} Массив найденных ошибок
 */
const validateHtml = (html, intl) => {
  const errors = [];

  // Базовые проверки
  if (!html || html.trim() === '') {
    errors.push({
      message: ERROR_MESSAGES.EMPTY_HTML(intl),
      position: DEFAULT_POSITION,
    });

    return errors;
  }

  if (!HTML_TAGS_PRESENCE_REGEX.test(html)) {
    errors.push({
      message: ERROR_MESSAGES.NOT_HTML_MARKUP(intl),
      position: DEFAULT_POSITION,
    });
    return errors;
  }

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Проверка базовой структуры
    if (!HTML_STRUCTURE_REGEX.test(html)) {
      errors.push({
        message: ERROR_MESSAGES.INVALID_STRUCTURE(intl),
        position: calculateErrPosition(html, 0),
      });
    }

    /**
     * Обрабатывает ошибки парсера DOM
     * Добавляет сообщения об ошибках синтаксиса
     * @returns {void} Ничего не возвращает
     */
    const handleParserErrors = () => {
      const parserErrors = doc.getElementsByTagName('parsererror');

      if (parserErrors.length > 0) {
        const errorText = parserErrors[0].textContent;
        const [ , line = 1 ] = errorText.match(/line (\d+)/i) || [];
        const [ , column = 1 ] = errorText.match(/column (\d+)/i) || [];

        errors.push({
          message: ERROR_MESSAGES.SYNTAX_ERROR(intl),
          details: errorText,
          position: { line: parseInt(line, 10), column: parseInt(column, 10) },
        });
      }
    };

    /**
     * Проверяет корректность закрывающего тега
     * @param {string} tag - Имя тега
     * @param {Object} position - Позиция в тексте
     * @param {Array} openTags - Стек открытых тегов
     * @param {Array} errors - Массив ошибок
     * @returns {void} Ничего не возвращает
     */
    const handleClosingTag = (tag, position, openTags, errors) => {
      if (openTags.length === 0) {
        errors.push({
          message: ERROR_MESSAGES.MISSING_OPENING_TAG(tag, intl),
          position,
        });
        return;
      }

      const lastOpenTag = last(openTags);

      if (lastOpenTag.tag === tag) {
        openTags.pop();
      } else {
        errors.push({
          message: ERROR_MESSAGES.WRONG_CLOSING_ORDER(lastOpenTag.tag, tag, intl),
          position,
        });
      }
    };

    /**
     * Проверяет наличие и корректность обязательных атрибутов
     * для HTML элементов согласно спецификации
     * @returns {void} Ничего не возвращает
     */
    const validateAttributes = () => {
      doc.body.innerHTML = html;

      Object.entries(REQUIRED_ATTRIBUTES).forEach(([ tag, attributes ]) => {
        const elements = doc.body.querySelectorAll(tag);

        elements.forEach((element) => {
          const elementHtml = element.outerHTML;
          const position = calculateErrPosition(html, html.indexOf(elementHtml));

          attributes.forEach((attr) => {
            if (!element.hasAttribute(attr)) {
              errors.push({
                message: ERROR_MESSAGES.MISSING_ATTRIBUTE(attr, tag, intl),
                element: elementHtml,
                position,
              });
            } else if (element.getAttribute(attr).trim() === '') {
              errors.push({
                message: ERROR_MESSAGES.EMPTY_ATTRIBUTE(attr, tag, intl),
                element: elementHtml,
                position,
              });
            }
          });
        });
      });
    };

    /**
     * Проверяет корректность вложенности тегов
     * Контролирует правильное расположение дочерних элементов
     * внутри родительских
     * @returns {void} Ничего не возвращает
     */
    const validateParentChild = () => {
      Object.entries(INVALID_PARENT_CHILD).forEach(([ child, validParents ]) => {
        const elements = doc.getElementsByTagName(child);

        Array.from(elements).forEach((element) => {
          const checkParent = (parent) => {
            if (!parent) {
              return false;
            }

            if (validParents.includes(parent.tagName.toLowerCase())) {
              return true;
            }
            return checkParent(parent.parentElement);
          };

          const hasValidParent = checkParent(element.parentElement);

          if (!hasValidParent) {
            errors.push({
              message: ERROR_MESSAGES.INVALID_PARENT(child, validParents, intl),
              element: element.outerHTML,
              position: calculateErrPosition(html, html.indexOf(element.outerHTML)),
            });
          }
        });
      });
    };

    /**
     * Проверяет значения атрибутов на соответствие
     * заданным регулярным выражениям
     * @returns {void} Ничего не возвращает
     */
    const validateAttributeValues = () => {
      Object.entries(ATTRIBUTE_PATTERNS).forEach(([ attr, pattern ]) => {
        const elements = doc.querySelectorAll(`[${attr}]`);

        elements.forEach((element) => {
          const value = element.getAttribute(attr);

          if (!pattern.test(value)) {
            errors.push({
              message: ERROR_MESSAGES.INVALID_ATTRIBUTE_VALUE(attr, pattern, intl),
              element: element.outerHTML,
              position: calculateErrPosition(html, html.indexOf(element.outerHTML)),
            });
          }
        });
      });
    };

    /**
     * Проверяет уникальность идентификаторов (id)
     * @returns {void} Ничего не возвращает
     */
    const validateUniqueIds = () => {
      const ids = new Set();
      const elements = doc.querySelectorAll('[id]');

      elements.forEach((element) => {
        const id = element.getAttribute('id');

        if (ids.has(id)) {
          errors.push({
            message: ERROR_MESSAGES.DUPLICATE_ID(id, intl),
            element: element.outerHTML,
            position: calculateErrPosition(html, html.indexOf(element.outerHTML)),
          });
        }
        ids.add(id);
      });
    };

    /**
     * Проверяет корректность открытия/закрытия тегов
     * и их последовательность
     * @returns {void} Ничего не возвращает
     */
    const validateTags = () => {
      const openTags = [];
      const validateHtmlTag = (regex, html, errors) => {
        const match = regex.exec(html);

        if (match === null) {
          // Проверка незакрытых тегов в конце
          openTags.forEach(({ tag, position }) => {
            errors.push({
              message: ERROR_MESSAGES.UNCLOSED_TAG(tag, intl),
              position,
            });
          });
          return;
        }

        const [ fullMatch, tag, selfClosing ] = match;
        const normalizedTag = tag.toLowerCase();
        const position = calculateErrPosition(html, match.index);

        if (!VALID_HTML_TAGS.includes(normalizedTag)) {
          errors.push({
            message: ERROR_MESSAGES.UNKNOWN_TAG(normalizedTag, intl),
            position,
          });
          validateHtmlTag(regex, html, errors);
          return;
        }

        const isClosingTag = fullMatch.startsWith('</');
        const isSelfClosing = selfClosing === '/' || [ 'img', 'br', 'hr', 'input', 'link', 'meta' ].includes(normalizedTag);

        if (!isClosingTag && !isSelfClosing) {
          openTags.push({ tag: normalizedTag, position });
        } else if (isClosingTag) {
          handleClosingTag(normalizedTag, position, openTags, errors);
        }
        validateHtmlTag(regex, html, errors);
      };

      validateHtmlTag(HTML_TAG_REGEX, html, errors);
    };

    handleParserErrors();
    validateTags();
    validateAttributes();
    validateParentChild();
    validateAttributeValues();
    validateUniqueIds();
  } catch (error) {
    errors.push({
      message: ERROR_MESSAGES.CRITICAL_ERROR(intl),
      details: error.message,
      position: DEFAULT_POSITION,
    });
  }

  return errors.sort((_a, _b) => _a.position.line - _b.position.line);
};

export default validateHtml;
